/**
 * Gasto energético: metabolismo basal (Mifflin-St Jeor), factores de actividad
 * y calorías objetivo según el objetivo de composición corporal.
 */
/** Multiplicadores clásicos del gasto sobre el metabolismo basal. */
export const ACTIVITY_FACTORS = Object.freeze({
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
});
/** Suelo de seguridad: nunca se recomiendan menos calorías que esto. */
export const MIN_SAFE_CALORIES = 1200;
/** Límites del déficit diario (kcal) admitidos para `goal: 'lose'`. */
export const DEFICIT_RANGE = Object.freeze({ default: 500, min: 250, max: 1000 });
/** Límites del superávit diario (kcal) admitidos para `goal: 'gain'`. */
export const SURPLUS_RANGE = Object.freeze({ default: 300, min: 150, max: 600 });
function assertPositive(value, name) {
    if (!Number.isFinite(value) || value <= 0) {
        throw new RangeError(`${name} debe ser un número finito mayor que 0`);
    }
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
/**
 * Metabolismo basal por la ecuación de Mifflin-St Jeor (1990):
 * hombres `10·kg + 6.25·cm − 5·años + 5`, mujeres `… − 161`.
 * Para `other` se promedian ambas variantes (equivale a `… − 78`).
 *
 * @returns kcal/día redondeadas al entero más cercano.
 */
export function bmrMifflinStJeor({ sex, weightKg, heightCm, ageYears }) {
    assertPositive(weightKg, 'weightKg');
    assertPositive(heightCm, 'heightCm');
    if (!Number.isFinite(ageYears) || ageYears < 0) {
        throw new RangeError('ageYears debe ser un número finito mayor o igual que 0');
    }
    const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
    const male = base + 5;
    const female = base - 161;
    const raw = sex === 'male' ? male : sex === 'female' ? female : (male + female) / 2;
    return Math.round(raw);
}
/** Multiplicador de actividad asociado a un nivel. */
export function activityFactor(level) {
    const factor = ACTIVITY_FACTORS[level];
    if (factor === undefined) {
        throw new RangeError(`activityLevel desconocido: ${String(level)}`);
    }
    return factor;
}
/** Gasto energético total diario: `basal × factor de actividad`, en kcal enteras. */
export function tdee(input) {
    return Math.round(bmrMifflinStJeor(input) * activityFactor(input.activityLevel));
}
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Edad en años cumplidos a partir de una fecha `YYYY-MM-DD`.
 * Usa el calendario local de `today` (por defecto, hoy). Sin librerías de fechas.
 *
 * @throws RangeError si el formato es inválido, la fecha no existe o es futura.
 */
export function ageFromBirthDate(birthDate, today = new Date()) {
    const match = ISO_DATE.exec(birthDate);
    if (!match) {
        throw new RangeError(`birthDate debe tener formato YYYY-MM-DD: ${birthDate}`);
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
        throw new RangeError(`birthDate no es una fecha válida: ${birthDate}`);
    }
    const ty = today.getFullYear();
    const tm = today.getMonth() + 1;
    const td = today.getDate();
    let age = ty - year;
    if (tm < month || (tm === month && td < day))
        age -= 1;
    if (age < 0) {
        throw new RangeError(`birthDate está en el futuro: ${birthDate}`);
    }
    return age;
}
/** Días del mes (1-12) teniendo en cuenta los años bisiestos del calendario gregoriano. */
export function daysInMonth(year, month) {
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
/**
 * Calorías objetivo diarias: `lose` resta el déficit (500 kcal por defecto,
 * limitado a 250..1000), `gain` suma el superávit (300 por defecto, 150..600)
 * y `maintain` mantiene el gasto. Nunca devuelve menos de {@link MIN_SAFE_CALORIES}.
 */
export function targetCalories({ tdee: total, goal, rate }) {
    if (!Number.isFinite(total) || total <= 0) {
        throw new RangeError('tdee debe ser un número finito mayor que 0');
    }
    let calories;
    if (goal === 'lose') {
        const deficit = clamp(rate ?? DEFICIT_RANGE.default, DEFICIT_RANGE.min, DEFICIT_RANGE.max);
        calories = total - deficit;
    }
    else if (goal === 'gain') {
        const surplus = clamp(rate ?? SURPLUS_RANGE.default, SURPLUS_RANGE.min, SURPLUS_RANGE.max);
        calories = total + surplus;
    }
    else {
        calories = total;
    }
    return Math.max(MIN_SAFE_CALORIES, Math.round(calories));
}
//# sourceMappingURL=tdee.js.map